/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Sparkles, MoreHorizontal, X, EyeOff } from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

/**
 * Shared, role-gated deterministic Pattern Hints banner — V1.2.
 * --------------------------------------------------------------
 * V1.0/1.1: Read-only display fed by GET /api/tracking/dashboard-signal/{id}.
 * V1.2 adds inline suppression for care-team + admin:
 *   • POST /api/tracking/pattern-hints/{id}/suppress
 *   • GET  /api/tracking/pattern-hints/{id}/suppressed
 *   • POST /api/tracking/pattern-hints/{id}/clear-suppression  (admin-only)
 *
 * Rules:
 *   • Suppress popover visible to admin / counselor / health_coach / doctor.
 *   • Suppress durations: 3 / 7 / 14 days only. Optional note (max 500).
 *   • Suppressed-list indicator + Clear action is admin-only.
 *   • Filtering happens server-side; we just refetch on mutations.
 *   • No subscriber UI change — banner already returns null for them.
 */

const SUPPRESS_ROLES = new Set(['admin', 'counselor', 'health_coach', 'doctor']);
const CACHE_TTL_MS = 60_000;
const _resultCache = new Map();   // subscriberId -> { ts, payload }
const _inflightCache = new Map(); // subscriberId -> Promise

async function fetchSignal(subscriberId, force = false) {
  if (!force) {
    const cached = _resultCache.get(subscriberId);
    if (cached && (Date.now() - cached.ts) < CACHE_TTL_MS) return cached.payload;
    if (_inflightCache.has(subscriberId)) return _inflightCache.get(subscriberId);
  } else {
    _resultCache.delete(subscriberId);
    _inflightCache.delete(subscriberId);
  }
  const promise = axios
    .get(`${API}/tracking/dashboard-signal/${subscriberId}`)
    .then((r) => {
      const payload = {
        hints: r.data?.pattern_hints || [],
        suppressedCount: r.data?.pattern_hints_suppressed_count || 0,
      };
      _resultCache.set(subscriberId, { ts: Date.now(), payload });
      return payload;
    })
    .catch(() => ({ hints: [], suppressedCount: 0 }))
    .finally(() => { _inflightCache.delete(subscriberId); });
  _inflightCache.set(subscriberId, promise);
  return promise;
}

function invalidateCache(subscriberId) {
  _resultCache.delete(subscriberId);
  _inflightCache.delete(subscriberId);
}

// ── Suppress popover for a single hint ──────────────────────────
function SuppressMenu({ subscriberId, hintId, onDone }) {
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(7);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/tracking/pattern-hints/${subscriberId}/suppress`,
        { hint_key: hintId, duration_days: duration, note: note.trim() || null },
      );
      toast.success(`Hint suppressed for ${duration} days`);
      setOpen(false);
      setNote('');
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not suppress hint';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`pattern-hint-suppress-btn-${hintId}`}
          className="ml-2 shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Suppress hint"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 bg-slate-900 border-white/10 text-white p-3"
        data-testid="pattern-hint-suppress-popover"
      >
        <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">
          Suppress this hint
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-2">
          {[3, 7, 14].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDuration(d)}
              data-testid={`pattern-hint-suppress-${d}d`}
              className={`text-xs rounded border px-2 py-1.5 transition-colors ${
                duration === d
                  ? 'border-cyan-400/50 bg-cyan-500/10 text-cyan-200'
                  : 'border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.05]'
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        <Textarea
          placeholder="Optional note (max 500)"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          rows={2}
          data-testid="pattern-hint-suppress-note"
          className="bg-white/[0.03] border-white/10 text-xs mb-2 resize-none"
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm" variant="ghost"
            onClick={() => setOpen(false)}
            className="text-white/60 hover:text-white/80 h-7 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy}
            data-testid="pattern-hint-suppress-confirm"
            className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 h-7 text-xs"
          >
            {busy ? 'Saving…' : 'Suppress'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Admin-only suppressed list popover ──────────────────────────
function SuppressedListPopover({ subscriberId, count, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tracking/pattern-hints/${subscriberId}/suppressed`);
      setItems(r.data?.active || []);
    } catch {
      setItems([]);
    }
  }, [subscriberId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const clear = async (hintKey) => {
    try {
      await axios.post(
        `${API}/tracking/pattern-hints/${subscriberId}/clear-suppression`,
        { hint_key: hintKey },
      );
      toast.success('Suppression cleared');
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear suppression');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="pattern-hint-suppressed-indicator"
          className="text-[11px] text-white/50 hover:text-white/80 inline-flex items-center gap-1 transition-colors"
        >
          <EyeOff className="w-3 h-3" />
          {count} suppressed {count === 1 ? 'hint' : 'hints'} — view
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 bg-slate-900 border-white/10 text-white p-0"
        data-testid="pattern-hint-suppressed-list"
      >
        <div className="p-3 border-b border-white/10 text-[11px] uppercase tracking-wider text-white/50">
          Suppressed hints (admin)
        </div>
        <div className="max-h-72 overflow-y-auto">
          {items === null && (
            <div className="p-3 text-xs text-white/40">Loading…</div>
          )}
          {items !== null && items.length === 0 && (
            <div className="p-3 text-xs text-white/40">No active suppressions.</div>
          )}
          {items?.map((s) => (
            <div
              key={s.id}
              data-testid={`pattern-hint-suppressed-row-${s.hint_key}`}
              className="px-3 py-2 border-b border-white/5 last:border-0 flex items-start justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs text-white/90 font-mono truncate">{s.hint_key}</div>
                <div className="text-[10px] text-white/50 mt-0.5">
                  {s.duration_days}d · until {new Date(s.suppressed_until).toLocaleString()}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  by {s.actor?.email || s.suppressed_by} ({s.suppressed_by_role})
                </div>
                {s.note && (
                  <div className="text-[11px] text-white/60 italic mt-1 line-clamp-2">
                    “{s.note}”
                  </div>
                )}
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => clear(s.hint_key)}
                data-testid={`pattern-hint-clear-suppression-${s.hint_key}`}
                className="text-rose-300 hover:text-rose-200 h-7 px-2 text-[11px] shrink-0"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function PatternHintsBanner({ subscriberId, viewerRole, className = '' }) {
  const allowed = SUPPRESS_ROLES.has(viewerRole);
  const isAdmin = viewerRole === 'admin';
  const [hints, setHints] = useState(null);
  const [suppressedCount, setSuppressedCount] = useState(0);

  const load = useCallback(async (force = false) => {
    if (!subscriberId) return;
    const payload = await fetchSignal(subscriberId, force);
    setHints(payload.hints || []);
    setSuppressedCount(payload.suppressedCount || 0);
  }, [subscriberId]);

  useEffect(() => {
    if (!allowed || !subscriberId) return;
    let cancelled = false;
    fetchSignal(subscriberId).then((payload) => {
      if (cancelled) return;
      setHints(payload.hints || []);
      setSuppressedCount(payload.suppressedCount || 0);
    });
    return () => { cancelled = true; };
  }, [subscriberId, allowed]);

  const onMutated = () => {
    invalidateCache(subscriberId);
    load(true);
  };

  if (!allowed || !subscriberId) return null;

  // Loading
  if (hints === null) {
    return (
      <div
        data-testid="pattern-hints-banner-loading"
        className={`rounded border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-white/40 ${className}`}
      >
        Loading pattern hints…
      </div>
    );
  }

  // Empty — distinguishes "all suppressed" vs "none yet"
  if (hints.length === 0) {
    const copy = suppressedCount > 0
      ? `No active tracking pattern. ${suppressedCount} suppressed.`
      : 'No tracking pattern yet.';
    return (
      <div
        data-testid="pattern-hints-banner-empty"
        className={`rounded border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-white/50 flex items-center justify-between gap-2 ${className}`}
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-white/30" />
          {copy}
        </span>
        {isAdmin && suppressedCount > 0 && (
          <SuppressedListPopover
            subscriberId={subscriberId}
            count={suppressedCount}
            onChange={onMutated}
          />
        )}
      </div>
    );
  }

  const top2 = hints.slice(0, 2);
  return (
    <div data-testid="pattern-hints-banner" className={`space-y-1.5 ${className}`}>
      {top2.map((h) => (
        <div
          key={h.id}
          data-testid={`pattern-hint-banner-${h.id}`}
          className={`flex items-start gap-2 text-[12px] leading-relaxed rounded border px-3 py-2 ${
            h.severity === 'concern'
              ? 'border-rose-500/25 bg-rose-500/5 text-rose-200'
              : h.severity === 'watch'
                ? 'border-amber-500/25 bg-amber-500/5 text-amber-200'
                : 'border-cyan-500/20 bg-cyan-500/5 text-cyan-200'
          }`}
        >
          <span className="text-[9px] uppercase tracking-wider opacity-70 shrink-0 mt-0.5">{h.severity}</span>
          <span className="flex-1">{h.text}</span>
          <SuppressMenu subscriberId={subscriberId} hintId={h.id} onDone={onMutated} />
        </div>
      ))}
      {isAdmin && suppressedCount > 0 && (
        <div className="pl-1 pt-0.5">
          <SuppressedListPopover
            subscriberId={subscriberId}
            count={suppressedCount}
            onChange={onMutated}
          />
        </div>
      )}
    </div>
  );
}
